import { useState, useCallback } from 'react';
export const useFileUpload = ({ rsocket, allowedTypes = [
    'jpg',
    'png',
    'gif',
    'pdf',
    'docx',
    'xlsx'
], maxSizeBytes = 26214400, requestTimeoutMs = 10000, getAccessToken })=>{
    const [state, setState] = useState({
        isUploading: false,
        progress: 0,
        error: null,
        uploadedFileUrl: null
    });
    const [objectUrl, setObjectUrl] = useState(null);
    const validateFile = useCallback((file)=>{
        const fileType = file.name.split('.').pop()?.toLowerCase();
        if (!fileType || !allowedTypes.includes(fileType)) {
            return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
        }
        if (file.size > maxSizeBytes) {
            return `File size must be under ${(maxSizeBytes / 1024 / 1024).toFixed(0)}MB`;
        }
        return null;
    }, [
        allowedTypes,
        maxSizeBytes
    ]);
    const uploadFile = useCallback(async (file)=>{
        const validationError = validateFile(file);
        if (validationError) {
            setState((prev)=>({
                    ...prev,
                    error: validationError,
                    isUploading: false
                }));
            const validationErr = new Error(validationError);
            validationErr.name = 'UploadValidationError';
            throw validationErr;
        }
        setState({
            isUploading: true,
            progress: 0,
            error: null,
            uploadedFileUrl: null
        });
        try {
            const requestPayload = {
                filename: file.name,
                sizeBytes: file.size,
                contentType: file.name.split('.').pop()?.toLowerCase() || ''
            };
            // Step 1: Get presigned URL from backend
            let timeoutId = null;
            const fetchUploadUrl = async ()=>{
                const uploadUrlResponse = await Promise.race([
                    rsocket.requestResponse('api.v1.files.getUploadUrl', requestPayload),
                    new Promise((_, reject)=>{
                        const timeoutError = new Error('Upload URL request timed out');
                        timeoutError.name = 'UploadUrlTimeout';
                        timeoutId = setTimeout(()=>reject(timeoutError), requestTimeoutMs);
                    })
                ]);
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                return uploadUrlResponse;
            };
            const performFallbackRequest = async ()=>{
                if (!getAccessToken) {
                    console.log('[useFileUpload] REST fallback unavailable: no token accessor');
                    throw new Error('Upload URL request timed out');
                }
                const token = getAccessToken();
                console.log('[useFileUpload] REST fallback token available:', Boolean(token));
                if (!token) {
                    throw new Error('Upload URL request timed out');
                }
                const params = new URLSearchParams({
                    filename: requestPayload.filename,
                    contentType: requestPayload.contentType,
                    sizeBytes: String(requestPayload.sizeBytes)
                });
                let response;
                try {
                    response = await fetch(`https://api.ugot.uk/api/media/presigned-upload?${params.toString()}`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: 'application/json'
                        }
                    });
                } catch (networkError) {
                    console.error('[useFileUpload] REST fallback network error', networkError);
                    throw networkError instanceof Error ? networkError : new Error('Fallback upload URL request network error');
                }
                if (!response.ok) {
                    console.error('[useFileUpload] REST fallback failed with status', response.status);
                    throw new Error(`Fallback upload URL request failed: ${response.status}`);
                }
                try {
                    return await response.json();
                } catch (parseError) {
                    console.error('[useFileUpload] REST fallback JSON parse error', parseError);
                    throw parseError instanceof Error ? parseError : new Error('Fallback upload URL response parse error');
                }
            };
            let uploadUrlResponse;
            try {
                uploadUrlResponse = await fetchUploadUrl();
                console.log('[useFileUpload] RSocket upload URL response', uploadUrlResponse);
            } catch (error) {
                const err = error instanceof Error ? error : new Error('Upload URL request failed');
                const isTimeout = err.name === 'UploadUrlTimeout';
                if (isTimeout) {
                    console.log('[useFileUpload] RSocket getUploadUrl request exceeded timeout; attempting REST fallback');
                    uploadUrlResponse = await performFallbackRequest();
                    console.log('[useFileUpload] REST fallback upload URL response', uploadUrlResponse);
                } else {
                    throw err;
                }
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            const { uploadUrl, fileId } = uploadUrlResponse;
            const normalizedUploadUrl = typeof uploadUrl === 'string' ? uploadUrl : '';
            console.log('[useFileUpload] Received upload URL', normalizedUploadUrl, 'fileId', fileId);
            // If the backend could not provide a real presigned URL, fall back to a local preview and
            // return the raw upload URL (or the preview URL) so the caller can continue the flow.
            if (!normalizedUploadUrl || normalizedUploadUrl.includes('media-server/upload')) {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                    setObjectUrl(null);
                }
                const errorMessage = '파일 업로드 URL을 가져오지 못했습니다. 잠시 후 다시 시도해주세요.';
                setState({
                    isUploading: false,
                    progress: 0,
                    error: errorMessage,
                    uploadedFileUrl: null
                });
                const unavailableError = new Error(errorMessage);
                unavailableError.name = 'UploadUrlUnavailable';
                throw unavailableError;
            }
            // Step 2: Upload file directly to storage via presigned URL using fetch
            console.log('[useFileUpload] Starting fetch PUT to', normalizedUploadUrl, 'size', file.size);
            setState((prev)=>({
                    ...prev,
                    progress: 5
                }));
            let response;
            try {
                response = await fetch(normalizedUploadUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': file.type
                    },
                    body: file
                });
            } catch (networkError) {
                console.error('[useFileUpload] Fetch upload network error', networkError);
                throw networkError instanceof Error ? networkError : new Error('Upload network error');
            }
            if (!response.ok) {
                const error = `Upload failed: ${response.status}`;
                console.error('[useFileUpload] Fetch upload failed with status', response.status);
                setState((prev)=>({
                        ...prev,
                        isUploading: false,
                        error
                    }));
                throw new Error(error);
            }
            let downloadUrl = null;
            try {
                const payload = await response.clone().json();
                downloadUrl = payload?.downloadUrl ?? payload?.mediaUrl ?? payload?.cdnUrl ?? payload?.url ?? null;
                if (downloadUrl) {
                    console.log('[useFileUpload] Upload response downloadUrl', downloadUrl);
                }
            } catch (parseError) {
                console.warn('[useFileUpload] Upload response JSON parse failed (expected for raw PUT responses)', parseError);
            }
            const mediaUrl = downloadUrl ?? `https://api.ugot.uk/api/media/${fileId}`;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                setObjectUrl(null);
            }
            setState({
                isUploading: false,
                progress: 100,
                error: null,
                uploadedFileUrl: mediaUrl
            });
            console.log('[useFileUpload] Upload completed successfully via fetch', mediaUrl);
            return {
                mediaUrl,
                fileId
            };
        } catch (error) {
            const upstreamError = error instanceof Error ? error : new Error('Unknown upload error');
            console.error('[useFileUpload] Upload failed', upstreamError);
            setState({
                isUploading: false,
                progress: 0,
                error: upstreamError.message,
                uploadedFileUrl: null
            });
            const wrappedError = new Error(upstreamError.message);
            wrappedError.name = upstreamError.name || 'UploadRequestError';
            throw wrappedError;
        }
    }, [
        rsocket,
        validateFile,
        objectUrl,
        requestTimeoutMs
    ]);
    const reset = useCallback(()=>{
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
        setState({
            isUploading: false,
            progress: 0,
            error: null,
            uploadedFileUrl: null
        });
        setObjectUrl(null);
    }, [
        objectUrl
    ]);
    return {
        ...state,
        uploadFile,
        reset
    };
};
